import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../../assets/images/logo.png";
import Seo from "../../components/Seo";
import { useGoogleSignIn } from "../../components/user/useGoogleSignIn";
import { apiErrorMessage, userAuth, userService } from "../../services/userApi";
import "../../components/lawyer/lawyer.css";
import "../../components/user/user.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const UserLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || userAuth.getPendingEmail());
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const google = useGoogleSignIn();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await userService.login(value);
      if (res?.data?.status) {
        toast.success(res.data.message || "OTP sent to your email");
        userAuth.setPendingEmail(value);
        navigate("/user/verify-otp", { state: { email: value, from: "login" } });
      } else {
        toast.error(res?.data?.message || "Unable to login.");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    try {
      const account = await google.signIn();
      const res = await userService.googleSignin(account);
      const data = res?.data?.data;
      if (res?.data?.status && data?.auth_token) {
        userAuth.setSession(data.auth_token, data.user);
        toast.success(res.data.message || "Signed in successfully");
        navigate("/user/dashboard", { replace: true });
      } else {
        toast.error(res?.data?.message || "Google sign-in failed.");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="lwp lwp-auth usp">
      <Seo title="Login | Legal Platform" noindex />
      <div className="lwp-auth-card">
        <img src={logo} alt="Legal Platform" className="lwp-auth-logo" />

        <h1 className="lwp-auth-title">Welcome Back!</h1>

        <form onSubmit={handleSubmit} noValidate>
          <input
            type="email"
            className="lwp-input"
            placeholder="Email*"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
          />
          <button type="submit" className="lwp-btn" disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm" /> : "Login"}
          </button>
        </form>

        <p className="lwp-auth-footer usp-auth-footer">
          Don&apos;t have an account? <Link to="/user/signup">Sign up</Link>
        </p>

        <div className="usp-auth-alt">
          {google.enabled && (
            <button
              type="button"
              className="usp-btn-ghost"
              onClick={handleGoogle}
              disabled={!google.ready || googleLoading}
            >
              <span className="usp-btn-ghost-icon">
                <GoogleIcon />
              </span>
              {googleLoading ? <span className="spinner-border spinner-border-sm" /> : "Continue with Google"}
            </button>
          )}
          <Link to="/lawyer/login" className="usp-btn-ghost">
            Lawyer Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;
